/**
 * What a person can see out of a car window.
 *
 * This is the human eye, not the camera network. It matters that the two are
 * different and that the eye is the weaker one, because the entire premise of
 * the game is that the network can answer questions your own eyes cannot.
 *
 * The rule, in plain terms: you can read a plate about five hundred feet down
 * a road you are on, and about a hundred feet across a corner, and not at all
 * through a block of houses.
 *
 * It is expressed against the road graph rather than against geometry, because
 * the graph is what the game has and because it happens to be the right model.
 * A road is a straight chord between two junctions, so anything on your road is
 * in your sight line by construction, and anything that is not on your road is
 * behind whatever is between the two roads.
 *
 * What a read gives you is a plate, a speed, and a road. What it never gives
 * you is where they are going. You can watch a car go past. You cannot watch
 * its route.
 */

import type { County } from './county.ts';
import type { Vehicle } from './sim.ts';

/**
 * How far down your own road you can read a plate. Five hundred feet.
 *
 * Generous for a human eye and deliberately so: this is the number the game
 * balances against, and the interesting comparison is with a camera, which
 * reads a plate at any hour in any weather and writes it down forever.
 */
export const READ_ALONG_ROAD_METERS = 152.4;

/**
 * How far you can read one across a corner. A hundred feet, which in practice
 * means you are both at the same intersection.
 */
export const READ_ACROSS_CORNER_METERS = 30.48;

/**
 * How far off your road's centerline something can be and still count as being
 * on your road. Wide enough for the far lane and a frontage road, narrow
 * enough that the next street over is behind the houses.
 */
export const SIGHT_CORRIDOR_METERS = 15;

/**
 * How long a plate stays in a player's head, in sim seconds.
 *
 * Three minutes. A person can hold a plate that long and then it goes, which
 * is the whole difference between a witness and a database. Kept on the client
 * because it is memory of something already lawfully seen; a player could
 * equally write it on their hand.
 */
export const PLATE_MEMORY_SECONDS = 180;

/** Unit vector along an edge, from its A end to its B end. */
function edgeDirection(county: County, edge: number): [number, number] {
  const a = county.edgeA[edge] as number;
  const b = county.edgeB[edge] as number;
  const dx = (county.nodeX[b] as number) - (county.nodeX[a] as number);
  const dy = (county.nodeY[b] as number) - (county.nodeY[a] as number);
  const length = Math.hypot(dx, dy) || 1;
  return [dx / length, dy / length];
}

/**
 * The roads an observer can see along.
 *
 * On a road, that is the road. Parked at a junction, it is every road meeting
 * there, because sitting at a crossroads you can look down all of them.
 */
function sightLines(county: County, observer: Vehicle, into: number[]): number[] {
  into.length = 0;
  if (observer.edge >= 0) {
    into.push(observer.edge);
    return into;
  }
  const node = observer.atNode;
  if (node < 0) return into;
  const start = county.adjStart[node] as number;
  const end = county.adjStart[node + 1] as number;
  for (let i = start; i < end; i++) into.push(county.adjEdge[i] as number);
  return into;
}

/** Whether two vehicles are on roads that meet at a junction. */
function meetAtAJunction(county: County, observer: Vehicle, target: Vehicle): boolean {
  if (target.edge < 0) {
    // Parked at a node: does the observer's road touch it?
    if (observer.edge < 0) return observer.atNode === target.atNode;
    return (
      (county.edgeA[observer.edge] as number) === target.atNode ||
      (county.edgeB[observer.edge] as number) === target.atNode
    );
  }
  const ta = county.edgeA[target.edge] as number;
  const tb = county.edgeB[target.edge] as number;
  if (observer.edge < 0) return observer.atNode === ta || observer.atNode === tb;
  if (observer.edge === target.edge) return true;
  const oa = county.edgeA[observer.edge] as number;
  const ob = county.edgeB[observer.edge] as number;
  return oa === ta || oa === tb || ob === ta || ob === tb;
}

const scratch: number[] = [];

/**
 * The shared geometry behind reading a plate and spotting a car to chase.
 *
 * Both are the same question asked at two ranges: is the target down a road I
 * am looking along, within so many meters of that road's line, and no farther
 * than `along` from me? Reading a plate is that question up close with a narrow
 * corridor; spotting a suspect to light up is the same look with a longer reach
 * and a wider corridor, because you can make out a car worth chasing from much
 * farther than you can make out six characters on its tag.
 */
function inSight(
  county: County,
  observer: Vehicle,
  target: Vehicle,
  along: number,
  corridor: number,
  corner: number,
): boolean {
  const dx = target.x - observer.x;
  const dy = target.y - observer.y;
  const distanceSquared = dx * dx + dy * dy;

  if (distanceSquared > along * along) return false;

  // Down a road you are on. What matters is how far the target sits off your
  // road's line, not how far along it: a road is straight between junctions,
  // so anything within the corridor is something you are looking straight at.
  for (const edge of sightLines(county, observer, scratch)) {
    const [ux, uy] = edgeDirection(county, edge);
    const across = Math.abs(dx * uy - dy * ux);
    if (across <= corridor) return true;
  }

  // Across a corner, and only from close enough that there is nothing between
  // the two of you.
  if (distanceSquared <= corner * corner && meetAtAJunction(county, observer, target)) {
    return true;
  }

  return false;
}

/**
 * Whether `observer` can read `target`'s plate right now.
 *
 * Authoritative. The server calls this and sends the plate only when it is
 * true, so a modified client cannot decide it can see further than it can.
 */
export function canReadPlate(county: County, observer: Vehicle, target: Vehicle): boolean {
  return inSight(
    county,
    observer,
    target,
    READ_ALONG_ROAD_METERS,
    SIGHT_CORRIDOR_METERS,
    READ_ACROSS_CORNER_METERS,
  );
}

/**
 * How far down a road an officer can pick out a car worth chasing, how wide
 * that look is, and how far across a corner. Deliberately much longer than the
 * plate-read numbers: lighting somebody up is a decision made on the make, the
 * color and the way it is being driven, from most of the way down the block --
 * the plate is what you confirm once you have closed on it, not what starts the
 * chase.
 */
export const SPOT_ALONG_ROAD_METERS = 600;
export const SPOT_CORRIDOR_METERS = 30;
export const SPOT_ACROSS_CORNER_METERS = 70;

/**
 * Whether `observer` can see `target` well enough to begin a pursuit.
 *
 * The long-range half of {@link canReadPlate}: same sight-line geometry, bigger
 * reach and a wider corridor. The server checks this when an officer lights
 * someone up, so a pursuit can only start on a car you actually have eyes on.
 */
export function canSpot(county: County, observer: Vehicle, target: Vehicle): boolean {
  return inSight(
    county,
    observer,
    target,
    SPOT_ALONG_ROAD_METERS,
    SPOT_CORRIDOR_METERS,
    SPOT_ACROSS_CORNER_METERS,
  );
}

/**
 * Whether you actually noticed the camera you just drove past.
 *
 * Spotting one used to be certain: get within SPOT_CAMERA_METERS on a road that
 * connects and it was yours. That is wrong, and wrong in a way that mattered,
 * because it made the map a function of *distance traveled* rather than of
 * attention. A grey box on a pole at the side of a road you are driving at
 * fifty is a thing people miss constantly, which is exactly why the real
 * crowdsourced maps are incomplete.
 *
 * So it is a chance per pass, and the things it depends on are the things that
 * would actually decide it:
 *
 * - **How close.** At arm's length it is obvious. At a hundred and thirty
 *   meters it is a smudge on a pole among other smudges on poles.
 * - **How fast.** Crawling through a junction is not the same as passing at
 *   highway speed with your eyes on the road.
 * - **Whether it is dark.** Same reason the cameras themselves get worse.
 *
 * ## The good part, which fell out rather than being designed
 *
 * Because it is per pass, exposure accumulates. Drive the same road every day
 * and you will eventually notice the thing; drive a road once and you probably
 * will not. So **you learn your own commute and you stay blind everywhere
 * else** -- which is precisely the bias section 8 says the community map has,
 * arrived at through the mechanism rather than imposed on it.
 *
 * And hunting deliberately has a price built in. Crawling the same stretch
 * repeatedly is the best way to find cameras and also the best way to put
 * yourself in the record over and over, which is the trade the whole game is
 * about.
 */

/** Best case: standing still, right next to it, in daylight. */
export const NOTICE_MAX_CHANCE = 0.55;

/**
 * Speed past which you are not really looking at the roadside.
 *
 * About forty miles an hour. Above this the chance is at its floor rather than
 * zero, because people do spot things at speed, just rarely.
 */
export const NOTICE_BLIND_SPEED = 18;

/** How much harder it is after dark. */
export const NOTICE_NIGHT_FACTOR = 0.45;

/**
 * The chance of noticing, for one pass.
 *
 * Returns 0 beyond `within`, so the caller can use it as the whole test.
 */
export function noticeChance(
  distanceMeters: number,
  speedMetersPerSecond: number,
  night: boolean,
  within = READ_ALONG_ROAD_METERS,
): number {
  if (distanceMeters > within) return 0;

  // Close is much better than far, and the falloff is steep rather than linear:
  // the difference between ten meters and thirty is far bigger than between
  // ninety and a hundred and ten.
  const closeness = Math.max(0, 1 - distanceMeters / within) ** 2;

  // Slow is better than fast, with a floor: people do spot things at speed.
  const attention = Math.max(0.15, 1 - speedMetersPerSecond / NOTICE_BLIND_SPEED);

  const light = night ? NOTICE_NIGHT_FACTOR : 1;
  return NOTICE_MAX_CHANCE * closeness * attention * light;
}
